import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { loginSliceValue } from "../redux/reducers/loginSlice";
import axios from "axios";
import { useSessionTimeout } from "../components/SessionTimeoutProvider";

interface Props {
  url: string;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headerContentType?: "application/json" | "multipart/form-data";
  preAuth?: boolean;
}

const useApi = ({
  url,
  method = "GET",
  headerContentType = "application/json",
  preAuth = false,
}: Props) => {
  const { userDetails } = useSelector(loginSliceValue);
  const [loading, setLoading] = useState(false);
  const { showTimeoutModal } = useSessionTimeout();

  const makeApiCall = useCallback(
    async <T, R>(
      input?: T,
      overrideUrl?: string
    ): Promise<{
      response: R | undefined;
      loading: boolean;
      error: { msg: string; message?: string; status: string } | undefined;
    }> => {
      setLoading(true);
      try {
        const config = {
          url: `${process.env.REACT_APP_BASE_URL + (overrideUrl || url)}`,
          method,
          data: input,
          headers: {
            "Content-Type": headerContentType,
            Authorization: "Bearer " + userDetails.token,
            // Authorization: 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY4MmIxNmY1NWYxODQwMTJiMGZmZDg0MyIsImlhdCI6MTc1NDg5MzM2NywiZXhwIjoxNzU0ODk2OTY3fQ.D3GtdCtnR9rqiMhNqjboFkY8wAA3kAsFuuUb-_Ty2h',
          },
        };

        if (!userDetails.token && !preAuth)
          return {
            response: undefined,
            loading: false,
            error: undefined,
          };

        const response = await axios(config);
        if (response) {
          return { response: response.data, loading, error: undefined };
        } else {
          return {
            response: undefined,
            loading: false,
            error: { msg: "Error fetching Data", status: "fail" },
          };
        }
      } catch (e: any) {
        const errorData = e.response?.data;
        const errorMsg = errorData?.msg || e.message || "";
        const errorStatus = errorData?.status || "";

        // Check for JWT expiration - specific status and message check
        const isJwtExpired =
          e.response?.status === 401 ||
          e.response?.status === 403 ||
          (errorStatus === "fail" && errorMsg === "jwt expired") ||
          errorMsg.toLowerCase().includes("jwt expired") ||
          errorMsg.toLowerCase().includes("token") ||
          errorMsg.toLowerCase().includes("authentication");

        if (isJwtExpired) {
          showTimeoutModal();
          // Return undefined error to prevent toast notifications
          return {
            response: undefined,
            loading: false,
            error: undefined, // Don't return error to prevent toast
          };
        }

        return {
          response: undefined,
          loading,
          error: e.response && e.response.data ? e.response.data : e,
        };
      } finally {
        setLoading(false);
      }
    },
    [userDetails, method, url, headerContentType, setLoading, showTimeoutModal]
  );

  return {
    loading,
    makeApiCall,
  };
};

export default useApi;
